import PropTypes from 'prop-types';
import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { useAnalytics } from 'use-analytics';
import { useMediaQuery } from 'react-responsive';
import Filter from '../../../../Filters/Filter';
import LayoverSlider from '../../FlightFilterBar/LayoverFlightFilter/LayoverSlider';
import flightSearchInput from '../../../../../helpers/selectors/flightSearchInput';
import { FlightSearchTypes, maxLayoverDuration, minLayoverDuration } from '../../../../../constants';
import { setFlightTripSearchInputs } from '../../../../../store/slices/tripSearch';
import { updateFlightSearchObj } from '../../../../../store/slices/flight';

const LayoverDurationInput = ({ filterMaxWidthClass, tripId, filterBgColor }) => {
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const isTripSearch = !tripId;
  const { searchInputs } = useSelector(flightSearchInput(tripId, isTripSearch));
  const isOneWay = searchInputs.flightSearchType === FlightSearchTypes.ONE_WAY;
  const isEmptyFilter = useMemo(() => !_.find(searchInputs.flightSegments,
    (fs) => fs.minLayoverDuration > minLayoverDuration
     || fs.maxLayoverDuration < maxLayoverDuration), [searchInputs.flightSegments]);

  const handleClear = useCallback(() => {
    const payload = {
      tripId,
      searchObj: {
        flightSegments: searchInputs.flightSegments ? [...searchInputs.flightSegments] : [],
      },
    };
    payload.searchObj.flightSegments.forEach((segment, index) => {
      payload.searchObj.flightSegments[index] = {
        ...payload.searchObj.flightSegments[index],
        minLayoverDuration,
        maxLayoverDuration,
      };
    });
    if (isTripSearch) dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
    else dispatch(updateFlightSearchObj(payload));
    track('LayoverDurationInput.handleClear', {
    });
  }, [dispatch, isTripSearch, searchInputs.flightSegments, track, tripId]);

  const handleChange = (event, newValue) => {
    const payload = {
      tripId,
      searchObj: {
        flightSegments: searchInputs.flightSegments ? [...searchInputs.flightSegments] : [],
      },
    };

    if (searchInputs.flightSearchType === FlightSearchTypes.ONE_WAY) {
      payload.searchObj.flightSegments[newValue.rowIndex] = {
        ...payload.searchObj.flightSegments[newValue.rowIndex],
        minLayoverDuration: newValue.min,
        maxLayoverDuration: newValue.max,
      };
    }
    if (searchInputs.flightSearchType === FlightSearchTypes.MULTI_CITY
      || searchInputs.flightSearchType === FlightSearchTypes.ROUND_TRIP) {
      payload.searchObj.flightSegments[newValue.rowIndex] = {
        ...payload.searchObj.flightSegments[newValue.rowIndex],
        minLayoverDuration: newValue.min,
        maxLayoverDuration: newValue.max,
      };
    }
    if (isTripSearch) dispatch(setFlightTripSearchInputs({ ...payload.searchObj }));
    else dispatch(updateFlightSearchObj(payload));
    track('LayoverDurationInput.handleChange', newValue);
  };

  const handleChangeWithIndex = (flightSegmentIndex) => (event, newValue) => {
    handleChange(event, { min: newValue[0], max: newValue[1], rowIndex: flightSegmentIndex });
  };

  const LayoverDurationSlider = (sliderName, flightSegmentIndex = 0) => (
    <LayoverSlider
      layover={[
        searchInputs.flightSegments && searchInputs.flightSegments[flightSegmentIndex]
          && ((searchInputs.flightSegments[flightSegmentIndex].minLayoverDuration != null)
            ? searchInputs.flightSegments[flightSegmentIndex]
              .minLayoverDuration : minLayoverDuration),
        searchInputs.flightSegments && searchInputs.flightSegments[flightSegmentIndex]
        && ((searchInputs.flightSegments[flightSegmentIndex].maxLayoverDuration != null)
          ? searchInputs.flightSegments[flightSegmentIndex]
            .maxLayoverDuration : maxLayoverDuration),
      ]}
      layoverMax={maxLayoverDuration}
      layoverMin={minLayoverDuration}
      onLayoverChange={handleChangeWithIndex(flightSegmentIndex)}
      name={sliderName}
    />
  );

  const isSmallScreen = useMediaQuery({ query: '(max-width: 768px)' });
  return (
    <Filter
      filterMaxWidthClass={filterMaxWidthClass}
      label="Layover Duration"
      menuWidthClass="w-max"
      isFilterEmpty={isEmptyFilter}
      handleClear={handleClear}
      disableCloseOnOutsideClick
      variant={isTripSearch ? undefined : 'outline'}
      bgClassName={!isTripSearch ? '' : filterBgColor}
      dropdownAligning={isSmallScreen ? 'right' : 'left'}
    >
      <div className="px-4 pb-1 pt-4">
        {searchInputs.flightSearchType !== FlightSearchTypes.MULTI_CITY ? (
          <>
            {LayoverDurationSlider('Outbound')}
            {isOneWay ? <></> : (
              <>
                {LayoverDurationSlider('Inbound', 1)}
              </>
            )}
          </>
        ) : searchInputs.flightSegments.map((segment, currentIndex) => (
          <div key={`${currentIndex.toString()}`}>
            {LayoverDurationSlider(`Flight ${currentIndex + 1}`, currentIndex)}
          </div>
        )) }
      </div>
    </Filter>
  );
};

LayoverDurationInput.propTypes = {
  tripId: PropTypes.string,
  filterMaxWidthClass: PropTypes.string,
  filterBgColor: PropTypes.string,
};

LayoverDurationInput.defaultProps = {
  tripId: undefined,
  filterBgColor: undefined,
  filterMaxWidthClass: 'max-w-full',
};

export default LayoverDurationInput;
